/**
 * Runtime configuration. One shape for every platform; each app resolves it
 * from its own source (Vite `import.meta.env`, Expo `EXPO_PUBLIC_*` + `extra`,
 * Tauri via the web bundle) and hands the result to `EchoProvider`.
 *
 * Environments mirror OnEarly's: `local` talks to a sibling `core` checkout,
 * `staging`/`production` to the hosted API. Values here are the defaults —
 * override per build, never edit in place for a one-off.
 *
 * OnEarly hosts: `api.oe.test` locally, and Core's global names hosted,
 * `api.staging.onearly.com` / `api.onearly.com`. The edge sends each request to the
 * caller's home region by their token, so the app calls one name wherever its people live;
 * Stronger Memory's group lives in the US region. A `421 WRONG_REGION` (a stale token's home)
 * is followed once by the API client. Staging web is appstaging.strongermemory.org (a
 * sub-zone of strongermemory.org delegated to OnEarly); production web is app.strongermemory.org.
 *
 * echo's own API (apps/api) is api-echo.staging.oe.app / api-echo.oe.app, in the US region
 * (platform PRD-2349, decided 2026-10-12); apistaging. / api.strongermemory.org come later.
 */
export type Environment = 'local' | 'staging' | 'production';

/**
 * Where OAuth tokens are refreshed and revoked. Core only mints the *initial* token pair
 * (`/api/v1/auth/login/verify` etc.); refresh goes straight to Ory Hydra's token endpoint
 * with the client Core issued the tokens for (`ORY_OAUTH_CLIENT_ID`) — the same values
 * OnEarly `app` ships in src/configs/*.json.
 */
export interface AuthConfig {
  tokenEndpoint: string;
  clientId: string;
  scopes: readonly string[];
}

/** The Reach a person uses: their home region's live stream (WebSocket) and the REST API. */
export interface ReachEndpoint {
  streamUrl: string;
  apiUrl: string;
}

/** What another region's Reach has of its own: its live stream. */
export interface ReachRegion {
  streamUrl: string;
}

/**
 * The Reach each region runs, as OnEarly `app` lists them (src/configs/*.json › reach). The API
 * is one for every home: hosted, Reach's global name, whose edge sends each call to the caller's
 * home region by their token. The stream is per region, because a WebSocket upgrade carries no
 * token for the edge to route by: the top level is Australia's, where a person whose token names
 * no home belongs, and `regions` holds the others by Core's region code (`us`). A person connects
 * to their own home Reach (`reachEndpointFor`); a group's thread may be homed elsewhere and uses
 * the broker link.
 */
export interface ReachConfig extends ReachEndpoint {
  regions: Readonly<Record<string, ReachRegion>>;
}

export interface AppConfig {
  environment: Environment;
  /** Local-only sample dashboard details; never replaces group membership or permissions. */
  mockGroups?: boolean;
  /** OnEarly Core REST API base URL (no trailing slash). */
  onearlyApiUrl: string;
  /** Group chat's Reach, per region (`reachEndpointFor`). */
  reach: ReachConfig;
  /** Echo's own lean API (optional — most data goes through OnEarly). */
  echoApiUrl: string | undefined;
  /** Canonical web origin, used for share links and deep links. */
  webOrigin: string;
  /** Native URL scheme for deep links (`stronger-memory://`). */
  appScheme: string;
  auth: AuthConfig;
  /**
   * Actor uuid of the PUBLIC "Stronger Memory" group every user joins on first sign-in —
   * v1's single-player mode is membership of this one global group (2026-09-02 sync).
   * `undefined` until the group exists in that environment; joining is then skipped.
   */
  strongerMemoryGroupActorUuid: string | undefined;
  /**
   * Uuids of the Stronger Memory content TYPEs in that environment's Core. A participant
   * cannot list them (`GET /content/types` is owner-only), so they are configuration,
   * printed by Core's `create-stronger-memory-content-types.ts` when it provisions them.
   * `undefined` until provisioned: the routine then runs without saving anything.
   */
  strongerMemoryTypes: StrongerMemoryTypes | undefined;
  posthog: { apiKey: string; host: string } | undefined;
  sentryDsn: string | undefined;
  /**
   * echo's release, the root package.json's version (release-please bumps it), baked into
   * each build and each EAS update, so a JavaScript-only update counts. Sent to Core as
   * `x-echo-version` (`clientHeaders`). Undefined where the build didn't say.
   */
  appVersion: string | undefined;
  /**
   * The commit the build or EAS update was made from, its first eight characters
   * (`resolveBuildMetadata` in `@echo/core/build`), and the UTC day it was made, `YYYY-MM-DD`.
   * Shown on Help & support (`versionDetails`). Undefined where the build didn't say.
   */
  buildHash: string | undefined;
  buildDate: string | undefined;
}

/** Content TYPE uuids the app writes and reads (docs/mvp/03-CONTENT.md). */
export interface StrongerMemoryTypes {
  /** Library container; one instance per edition, CONTAINS the sheets and prompts. */
  workbook: string;
  sheet: string;
  prompt: string;
  /** Reading passages; optional until every environment has them provisioned. */
  passage?: string;
  /**
   * The read-aloud library (scripts/seeds/read-aloud): its container, the search handle that
   * picks it out, and the text of each passage. Optional until the environment has it.
   */
  library?: string;
  libraryKey?: string;
  passageText?: string;
  /** A person's shelf (Library › My favorites); optional until the environment has it. */
  shelf?: string;
  /**
   * A person's settings on their account (`SM Preferences`). Exercise duration stays on the
   * device where it is not set.
   */
  preferences?: string;
  /**
   * A row of the Math library (scripts/seeds/quick-math). Library › Math and Quick math's
   * chooser show only where it is set.
   */
  mathSet?: string;
  session: string;
  attempt: string;
  answer: string;
  summary: string;
}

export const ENVIRONMENTS: readonly Environment[] = ['local', 'staging', 'production'];

const OAUTH_SCOPES = ['openid', 'email', 'profile', 'offline_access'] as const;

export const DEFAULT_CONFIG: Record<Environment, AppConfig> = {
  local: {
    environment: 'local',
    onearlyApiUrl: 'https://api.oe.test',
    echoApiUrl: 'http://localhost:4100',
    webOrigin: 'http://localhost:5173',
    appScheme: 'stronger-memory-local',
    auth: {
      tokenEndpoint: 'https://auth.oe.test/oauth2/token',
      clientId: 'e299297d-b93b-457b-bea8-11cd988a7afa',
      scopes: OAUTH_SCOPES,
    },
    // Created by hand in the local Core (slug `stronger-memory`, PUBLIC). Not seeded yet.
    strongerMemoryGroupActorUuid: 'e5fc2a30-0312-4824-b4d6-29457ac4dbb4',
    // Provisioned by hand in the local Core on 2026-09-04 (see docs/mvp/03-CONTENT.md).
    strongerMemoryTypes: {
      workbook: '68f887f6-ddd1-46d7-b2fe-4279468560b9',
      sheet: '5f60b0c2-7dc3-446c-816b-582167fb36ae',
      prompt: 'a2096547-42b2-4cd4-bbfd-b8eea8e4c85d',
      passage: '34c71f6b-3c07-48f4-9cbe-20e062c197bc',
      session: '2f134b0f-21bd-4a70-b8b7-577890df4c33',
      attempt: 'edf62913-c9e8-43c0-a0bf-5bd9c8c02974',
      answer: '391e7fff-4002-452d-acf2-2df282dc33d5',
      summary: 'a7952e1f-606d-43d4-9f27-ded7f24dbce5',
    },
    posthog: undefined,
    sentryDsn: undefined,
    appVersion: undefined,
    buildHash: undefined,
    buildDate: undefined,
    // A local Reach checkout, as `app` local.json.
    reach: { streamUrl: 'wss://reach.test/stream', apiUrl: 'https://reach.test/api', regions: {} },
  },
  staging: {
    environment: 'staging',
    onearlyApiUrl: 'https://api.staging.onearly.com',
    echoApiUrl: 'https://api-echo.staging.oe.app',
    webOrigin: 'https://appstaging.strongermemory.org',
    appScheme: 'stronger-memory-staging',
    auth: {
      tokenEndpoint: 'https://auth.staging.onearly.com/oauth2/token',
      clientId: '1a228c93-2de7-4bf2-b2ab-954278a88ce4',
      scopes: OAUTH_SCOPES,
    },
    // Group `stronger-memory` (PUBLIC) and types provisioned on staging on 2026-09-04 with
    // scripts/seeds/provision-via-api.mjs.
    strongerMemoryGroupActorUuid: 'becb8d79-bda3-47ed-8269-067307912cc7',
    strongerMemoryTypes: {
      workbook: 'f0399ccc-449e-4972-8634-717cae6bf639',
      sheet: '725b39bb-e50f-4082-b7c4-ce0f0b020ab3',
      prompt: '98c556b1-6d53-46c4-a18b-05c9a7ee406e',
      // The read-aloud library v1.0 (20 public-domain poems and stories) since 2026-09-28;
      // five placeholder passages before that.
      passage: 'a6c276d0-7800-4c1a-adbc-890dfadc837b',
      // The read-aloud library (100 stories and nature essays) since 2026-10-02, provisioned
      // with scripts/seeds/read-aloud/provision.mjs.
      library: '6f33b041-7b9b-4089-a69e-69f60d0497c9',
      libraryKey: '6bafbd5f-c61d-4507-82cf-cffd3350841b',
      passageText: '3964fa03-4697-4911-996e-0019bf6dd1e0',
      shelf: '747ad2aa-4d47-4769-81ba-e6d66bb87613',
      // The Math library (24 rows, 194 sheets) since 2026-10-05: scripts/seeds/quick-math.
      // Library › Math and Quick math's chooser show only where `mathSet` is set.
      mathSet: 'ba23ebb7-6768-423c-aac3-3d823b255a71',
      session: '1017f7f0-86c9-43d1-9488-29739e596a54',
      attempt: 'ab1cc143-accd-420b-8db6-93a9431d4c0c',
      answer: '9a68d5c4-c60d-41ba-8334-514b27afe575',
      summary: '747e7fbc-c462-43b7-9fb2-6b6ce6369df0',
    },
    posthog: undefined,
    sentryDsn: undefined,
    appVersion: undefined,
    buildHash: undefined,
    buildDate: undefined,
    reach: {
      streamUrl: 'wss://au.s.justreach.ai/stream',
      apiUrl: 'https://s.justreach.ai/api',
      regions: { us: { streamUrl: 'wss://us.s.justreach.ai/stream' } },
    },
  },
  production: {
    environment: 'production',
    onearlyApiUrl: 'https://api.onearly.com',
    echoApiUrl: 'https://api-echo.oe.app',
    webOrigin: 'https://app.strongermemory.org',
    appScheme: 'stronger-memory',
    auth: {
      tokenEndpoint: 'https://auth.onearly.com/oauth2/token',
      clientId: 'd3cb27a9-30d6-4b53-8ca5-7a30b3a0a3d7',
      scopes: OAUTH_SCOPES,
    },
    // StrongerMemory (PUBLIC), provisioned on 2026-09-21 (docs/mvp/03-CONTENT.md).
    strongerMemoryGroupActorUuid: '80c2626f-269a-4fb6-a2ce-9b219487bce6',
    strongerMemoryTypes: {
      workbook: '8d71c842-d232-4a9b-9699-449a37594439',
      sheet: 'd7321f11-d704-4a0b-9f10-dbe855e02510',
      prompt: '888d5f0a-5f89-4a25-a6c1-6c5c2debfec4',
      session: '88b41403-e834-4568-a324-33a545879cfd',
      attempt: '72917d47-f2bd-4d3c-8e61-4db7ecd4b312',
      answer: 'da850edb-ec14-4381-bae5-3778ca752f9e',
      summary: 'c2e0a19a-f71c-465f-accf-0e3739e46ec3',
      // Holds the five placeholder passages from staging (in SM Workbook, read by installed
      // builds) and the read-aloud library's passages.
      passage: '2362ca4e-1a03-486f-adc9-b697126e42b6',
      // The read-aloud library (100 stories and nature essays) since 2026-10-05, provisioned
      // with scripts/seeds/read-aloud/provision.mjs.
      library: '7264dfb1-9bd0-4acc-9672-9c881e966d32',
      libraryKey: '2dac4fdf-1b5c-4be4-919c-265cf1a93ff2',
      passageText: 'c856646c-0c83-4ec7-b4ca-9dabbfeeb176',
      shelf: '55713324-5ffa-4dc8-9a3a-82ade92aabc0',
      // The Math library (24 rows, 194 sheets) since 2026-10-06: scripts/seeds/quick-math.
      mathSet: '189cd2db-f005-4277-a73a-281c09d80141',
    },
    posthog: undefined,
    sentryDsn: undefined,
    appVersion: undefined,
    buildHash: undefined,
    buildDate: undefined,
    reach: {
      streamUrl: 'wss://au.justreach.ai/stream',
      apiUrl: 'https://justreach.ai/api',
      regions: { us: { streamUrl: 'wss://us.justreach.ai/stream' } },
    },
  },
};

/**
 * The Reach that serves a person: the shared API, and their home region's stream
 * (`homeRegionOfToken`). A home with its own entry in `regions` gets its stream; any other home,
 * or none, gets Australia's, as OnEarly `app`'s `reachEndpoint` does.
 */
export function reachEndpointFor(reach: ReachConfig, home: string | undefined): ReachEndpoint {
  const regional = home ? reach.regions[home] : undefined;
  return { streamUrl: regional?.streamUrl ?? reach.streamUrl, apiUrl: reach.apiUrl };
}

export function isEnvironment(value: unknown): value is Environment {
  return typeof value === 'string' && (ENVIRONMENTS as readonly string[]).includes(value);
}

/**
 * Whether a build may offer the en-XA pseudo-locale ("Pseudo (troubleshooting)") in its
 * language pickers and accept it from `?lang=` or `EXPO_PUBLIC_LOCALE`. Local builds only:
 * staging is what Goodwin and QA use as the product, and a troubleshooting language in its
 * picker read to them as a bug.
 */
export function allowsPseudoLocale(environment: Environment): boolean {
  return environment === 'local';
}

/**
 * Merge build-time overrides onto the environment defaults. `undefined`
 * overrides are ignored so callers can pass raw `process.env` reads.
 */
export function resolveConfig(
  environment: string | undefined,
  overrides: Partial<Record<keyof AppConfig, unknown>> = {},
): AppConfig {
  const env: Environment = isEnvironment(environment) ? environment : 'local';
  const base = DEFAULT_CONFIG[env];
  const merged: AppConfig = { ...base };
  for (const key of Object.keys(overrides) as (keyof AppConfig)[]) {
    const value = overrides[key];
    if (value !== undefined && value !== '') {
      (merged as unknown as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}

/**
 * The read-aloud library's types for a Core the defaults don't know (a local Core once it is
 * provisioned, or the e2e suite's stub), from one JSON env value:
 * `{"library":"…","libraryKey":"…","passageText":"…","shelf":"…","mathSet":"…","preferences":"…"}`. Ignored when unreadable or when the
 * environment has no content types at all.
 */
export function withLibraryTypes(config: AppConfig, raw: string | undefined): AppConfig {
  if (!raw || !config.strongerMemoryTypes) return config;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return config;
  }
  if (!parsed || typeof parsed !== 'object') return config;
  const pick = (
    key: 'library' | 'libraryKey' | 'passageText' | 'shelf' | 'mathSet' | 'preferences',
  ) => {
    const value = (parsed as Record<string, unknown>)[key];
    return typeof value === 'string' && value ? { [key]: value } : {};
  };
  return {
    ...config,
    strongerMemoryTypes: {
      ...config.strongerMemoryTypes,
      ...pick('library'),
      ...pick('libraryKey'),
      ...pick('passageText'),
      ...pick('shelf'),
      ...pick('mathSet'),
      ...pick('preferences'),
    },
  };
}
